import React, { useEffect, useState, useMemo } from 'react';
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Alert,
  CircularProgress,
  Chip,
  Avatar,
  Typography,
  TablePagination,
  TableSortLabel,
  InputAdornment,
  FormControlLabel,
  Switch,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  PersonAdd as PersonAddIcon,
  Search as SearchIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
} from '@mui/icons-material';
import Tooltip from '@mui/material/Tooltip';
import { usersService, CreateUserDto, UpdateUserDto } from '../services/usersService';
import { User } from '../types';

type SortKey = 'name' | 'email' | 'via' | 'status' | 'createdAt' | 'lastActiveAt';

const isPremiumUser = (user: User) => !!user.isPremium || user.role === 'admin';

/** Valor de cada coluna para ordenar; vazio (nunca acessou) fica por último */
const sortValue = (user: User, key: SortKey): string | number | null => {
  switch (key) {
    case 'name':
      return user.name.toLowerCase();
    case 'email':
      return user.email.toLowerCase();
    case 'via':
      return user.googleId ? 'google' : 'email';
    case 'status':
      return isPremiumUser(user) ? 1 : 0;
    case 'createdAt':
      return new Date(user.createdAt).getTime();
    case 'lastActiveAt':
      return user.lastActiveAt ? new Date(user.lastActiveAt).getTime() : null;
  }
};

const Users: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [formData, setFormData] = useState<CreateUserDto & { isPremium?: boolean }>({
    email: '',
    name: '',
    password: '',
    role: 'user',
    isPremium: false,
  });

  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Search
  const [searchTerm, setSearchTerm] = useState('');

  // Ordenação: mais novos primeiro, para achar cadastros recentes
  const [orderBy, setOrderBy] = useState<SortKey>('createdAt');
  const [order, setOrder] = useState<'asc' | 'desc'>('desc');

  const handleSort = (key: SortKey) => {
    if (orderBy === key) {
      setOrder(order === 'asc' ? 'desc' : 'asc');
    } else {
      setOrderBy(key);
      // Datas começam do mais recente; texto, de A a Z
      setOrder(key === 'createdAt' || key === 'lastActiveAt' || key === 'status' ? 'desc' : 'asc');
    }
    setPage(0);
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  // Filter and sort users
  const filteredUsers = useMemo(() => {
    let result = [...users];

    // Filter by search term
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      result = result.filter(
        (user) =>
          user.name.toLowerCase().includes(search) ||
          user.email.toLowerCase().includes(search)
      );
    }

    const direction = order === 'asc' ? 1 : -1;
    result.sort((a, b) => {
      const va = sortValue(a, orderBy);
      const vb = sortValue(b, orderBy);
      if (va === vb) return a.name.localeCompare(b.name, 'pt-BR');
      if (va === null) return 1;
      if (vb === null) return -1;
      if (typeof va === 'string' && typeof vb === 'string') {
        return va.localeCompare(vb, 'pt-BR') * direction;
      }
      return (va < vb ? -1 : 1) * direction;
    });

    return result;
  }, [users, searchTerm, orderBy, order]);

  // Paginated users
  const paginatedUsers = useMemo(() => {
    const start = page * rowsPerPage;
    return filteredUsers.slice(start, start + rowsPerPage);
  }, [filteredUsers, page, rowsPerPage]);

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const data = await usersService.getAll();
      setUsers(data);
      setError('');
    } catch (err: any) {
      setError('Erro ao carregar usuários');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (user?: User) => {
    if (user) {
      setEditingUser(user);
      setFormData({
        email: user.email,
        name: user.name,
        password: '',
        role: user.role,
        isPremium: user.isPremium || false,
      });
    } else {
      setEditingUser(null);
      setFormData({
        email: '',
        name: '',
        password: '',
        role: 'user',
        isPremium: false,
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingUser(null);
    setFormData({
      email: '',
      name: '',
      password: '',
      role: 'user',
      isPremium: false,
    });
  };

  const handleSubmit = async () => {
    try {
      if (editingUser) {
        // Atualizar usuário
        const updateData: UpdateUserDto = {
          email: formData.email,
          name: formData.name,
          role: formData.role,
          isPremium: formData.isPremium,
        };

        // Só incluir senha se foi preenchida
        if (formData.password) {
          updateData.password = formData.password;
        }

        await usersService.update(editingUser.id, updateData);
        setSuccess('Usuário atualizado com sucesso!');
      } else {
        // Criar novo usuário
        if (!formData.password) {
          setError('Senha é obrigatória para novos usuários');
          return;
        }
        await usersService.create(formData);
        setSuccess('Usuário criado com sucesso!');
      }

      handleCloseDialog();
      fetchUsers();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erro ao salvar usuário');
      setTimeout(() => setError(''), 3000);
    }
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Tem certeza que deseja excluir este usuário?')) {
      try {
        await usersService.delete(id);
        setSuccess('Usuário excluído com sucesso!');
        fetchUsers();
        setTimeout(() => setSuccess(''), 3000);
      } catch (err: any) {
        setError('Erro ao excluir usuário');
        setTimeout(() => setError(''), 3000);
      }
    }
  };

  const handleRemovePremium = async (user: User) => {
    const message =
      `Remover o premium de ${user.name}?\n\n` +
      `Isso vai cancelar a assinatura no Asaas (se houver) e ` +
      `zerar o status premium imediatamente.`;
    if (!window.confirm(message)) return;

    try {
      await usersService.removePremium(user.id);
      setSuccess(`Premium removido de ${user.name}`);
      fetchUsers();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erro ao remover premium');
      setTimeout(() => setError(''), 3000);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box
        sx={{
          mb: 4,
          pb: 3,
          borderBottom: '2px solid',
          borderColor: 'primary.main',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 600, color: '#2D3748', mb: 1 }}>
            Gestão de Usuários
          </Typography>
          <Typography variant="body1" sx={{ color: '#718096' }}>
            Gerencie os usuários do sistema
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<PersonAddIcon />}
          onClick={() => handleOpenDialog()}
          sx={{
            background: 'linear-gradient(135deg, #4CAF50 0%, #66BB6A 100%)',
            color: 'white',
            px: 3,
            py: 1.5,
            borderRadius: 2,
            textTransform: 'none',
            fontSize: '1rem',
            fontWeight: 500,
            boxShadow: '0 4px 12px rgba(76, 175, 80, 0.3)',
            '&:hover': {
              background: 'linear-gradient(135deg, #388E3C 0%, #4CAF50 100%)',
              boxShadow: '0 6px 16px rgba(76, 175, 80, 0.4)',
            },
          }}
        >
          Novo Usuário
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {/* Search */}
      <TextField
        placeholder="Buscar por nome ou email..."
        value={searchTerm}
        onChange={(e) => {
          setSearchTerm(e.target.value);
          setPage(0);
        }}
        size="small"
        sx={{ mb: 3, width: { xs: '100%', sm: 350 } }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon sx={{ color: '#718096' }} />
            </InputAdornment>
          ),
        }}
      />

      <TableContainer
        component={Paper}
        sx={{
          borderRadius: 2,
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
        }}
      >
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#F7FAFC' }}>
              {(
                [
                  ['name', 'Usuário'],
                  ['email', 'Email'],
                  ['via', 'Entrou com'],
                  [null, 'Papel'],
                  ['status', 'Status'],
                  ['createdAt', 'Criado em'],
                  ['lastActiveAt', 'Último acesso'],
                ] as [SortKey | null, string][]
              ).map(([key, label]) => (
                <TableCell
                  key={label}
                  sortDirection={key && orderBy === key ? order : false}
                  sx={{ fontWeight: 600, color: '#2D3748', whiteSpace: 'nowrap' }}
                >
                  {key ? (
                    <TableSortLabel
                      active={orderBy === key}
                      direction={orderBy === key ? order : 'asc'}
                      onClick={() => handleSort(key)}
                    >
                      {label}
                    </TableSortLabel>
                  ) : (
                    label
                  )}
                </TableCell>
              ))}
              <TableCell align="right" sx={{ fontWeight: 600, color: '#2D3748' }}>
                Ações
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedUsers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4, color: '#718096' }}>
                  {searchTerm ? 'Nenhum usuário encontrado para esta busca' : 'Nenhum usuário encontrado'}
                </TableCell>
              </TableRow>
            ) : (
              paginatedUsers.map((user) => (
                <TableRow
                  key={user.id}
                  sx={{
                    '&:hover': {
                      backgroundColor: '#F7FAFC',
                    },
                  }}
                >
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Avatar sx={{ bgcolor: 'primary.main' }}>
                        {user.name.charAt(0).toUpperCase()}
                      </Avatar>
                      <Typography sx={{ fontWeight: 500 }}>{user.name}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell sx={{ color: '#718096' }}>{user.email}</TableCell>
                  <TableCell sx={{ color: '#718096', whiteSpace: 'nowrap' }}>
                    {user.googleId ? 'Google' : 'E-mail'}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={user.role === 'admin' ? 'Administrador' : 'Usuário'}
                      size="small"
                      color={user.role === 'admin' ? 'primary' : 'default'}
                      sx={{
                        fontWeight: 500,
                        borderRadius: 1.5,
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    {isPremiumUser(user) ? (
                      <Chip
                        icon={<StarIcon sx={{ fontSize: 16 }} />}
                        label="Premium"
                        size="small"
                        sx={{
                          fontWeight: 500,
                          borderRadius: 1.5,
                          bgcolor: '#FFF3E0',
                          color: '#E65100',
                          '& .MuiChip-icon': { color: '#FFB300' },
                        }}
                      />
                    ) : (
                      <Chip
                        label="Gratuito"
                        size="small"
                        variant="outlined"
                        sx={{
                          fontWeight: 500,
                          borderRadius: 1.5,
                          color: '#718096',
                          borderColor: '#E2E8F0',
                        }}
                      />
                    )}
                  </TableCell>
                  <TableCell sx={{ color: '#718096', whiteSpace: 'nowrap' }}>{formatDate(user.createdAt)}</TableCell>
                  <TableCell sx={{ color: '#718096', whiteSpace: 'nowrap' }}>
                    {user.lastActiveAt ? formatDate(user.lastActiveAt) : 'Nunca'}
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Editar">
                      <IconButton
                        size="small"
                        onClick={() => handleOpenDialog(user)}
                        sx={{
                          color: 'primary.main',
                          '&:hover': { backgroundColor: 'primary.lighter' },
                        }}
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    {user.isPremium && (
                      <Tooltip title="Remover premium">
                        <IconButton
                          size="small"
                          onClick={() => handleRemovePremium(user)}
                          sx={{
                            color: '#E65100',
                            ml: 1,
                            '&:hover': { backgroundColor: '#FFF3E0' },
                          }}
                        >
                          <StarBorderIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                    <Tooltip title="Excluir">
                      <IconButton
                        size="small"
                        onClick={() => handleDelete(user.id)}
                        sx={{
                          color: 'error.main',
                          ml: 1,
                          '&:hover': { backgroundColor: 'error.lighter' },
                        }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      <TablePagination
        component="div"
        count={filteredUsers.length}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[10, 25, 50, 100]}
        labelRowsPerPage="Por página:"
        labelDisplayedRows={({ from, to, count }) =>
          `${from}-${to} de ${count !== -1 ? count : `mais de ${to}`}`
        }
        sx={{
          borderTop: '1px solid #e0e0e0',
          '.MuiTablePagination-toolbar': {
            px: 2,
          },
        }}
      />

      {/* Dialog para criar/editar usuário */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 600, color: '#2D3748' }}>
          {editingUser ? 'Editar Usuário' : 'Novo Usuário'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
            <TextField
              label="Nome"
              fullWidth
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Email"
              type="email"
              fullWidth
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label={editingUser ? 'Nova Senha (deixe em branco para manter)' : 'Senha'}
              type="password"
              fullWidth
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              InputLabelProps={{ shrink: true }}
              helperText={editingUser ? 'Deixe em branco se não quiser alterar a senha' : 'Mínimo 6 caracteres'}
            />
            <TextField
              label="Papel"
              select
              fullWidth
              value={formData.role}
              onChange={(e) => setFormData({ ...formData, role: e.target.value })}
              InputLabelProps={{ shrink: true }}
            >
              <MenuItem value="user">Usuário</MenuItem>
              <MenuItem value="admin">Administrador</MenuItem>
            </TextField>

            {editingUser && (
              <Box sx={{
                p: 2,
                bgcolor: '#FFF8E1',
                borderRadius: 2,
                border: '1px solid #FFE082'
              }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.isPremium || false}
                      onChange={(e) => setFormData({ ...formData, isPremium: e.target.checked })}
                      sx={{
                        '& .MuiSwitch-switchBase.Mui-checked': {
                          color: '#FFB300',
                        },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                          backgroundColor: '#FFB300',
                        },
                      }}
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <StarIcon sx={{ color: '#FFB300', fontSize: 20 }} />
                      <Typography sx={{ fontWeight: 500 }}>Premium Manual</Typography>
                    </Box>
                  }
                />
                <Typography variant="caption" sx={{ color: '#8D6E63', display: 'block', mt: 0.5 }}>
                  Ative para conceder acesso premium sem assinatura (beta testers)
                </Typography>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCloseDialog} sx={{ color: '#718096' }}>
            Cancelar
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            sx={{
              background: 'linear-gradient(135deg, #4CAF50 0%, #66BB6A 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #388E3C 0%, #4CAF50 100%)',
              },
            }}
          >
            {editingUser ? 'Salvar' : 'Criar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Users;
